import { INDICATION_FIELD_LABELS, isIcd10InDefinedList } from '../dde'
import type { DDERow, IndicationSetDiff } from '../dde'
import type { Indication, IndicationIcd10Code } from '../types'
import { addedIndicationSchema } from './addedIndication'
import { enforcedStandard } from './intervals'
import {
  applyConfigurationOperation,
  operationFieldValue,
  operationFields,
} from './operations'
import { normalizeScopedOverride } from './persistence'
import { reconcileConfiguration, same } from './reconcile'
import type { VersionCompareGroup } from './versionMap'
import {
  CONFIGURATION_OPERATION_SCHEMA_VERSION,
  type ConfigurationOperation,
  type ConfigurationScopedOverride,
  type OperationActor,
  type ReconciledOperation,
  type StandardRevision,
} from './types'
import { compareCodeUnits } from './canonical'

export const MOVE_DECISIONS = [
  'keep',
  'drop',
  'take_standard',
  'keep_as_added',
  'keep_as_modification',
] as const

export type MoveDecision = (typeof MOVE_DECISIONS)[number]

export type MoveOutcome =
  | 'still_applies'
  | 'carries_over'
  | 'now_matches_standard'
  | 'no_longer_needed'
  | 'conflict_field'
  | 'conflict_removed'
  | 'conflict_collision'

/** How one override operation, or one added/not-applied indication, lands on the newer Standard. */
export interface MoveRow {
  id: string
  indicationId: string
  indicationName: string
  connectorIds: string[]
  field: string
  overrideValue: string
  fromValue: string
  toValue: string
  outcome: MoveOutcome
  /** Absent on conflicts: the author must choose. */
  defaultDecision?: MoveDecision
  allowedDecisions: MoveDecision[]
  rationale?: string
  standardChangedAround?: boolean
  /** Revision numbers of the Standard steps that changed what this row's own fields cover. Only on a multi-step move. */
  changedIn?: number[]
  /** Revision numbers of the Standard steps that changed anything on the row's indication. Only on a multi-step move. */
  indicationChangedIn?: number[]
}

export interface MoveReadiness {
  status: 'ready' | 'review' | 'conflicts'
  conflicts: number
  carryOver: number
  changedAround: number
  redundant: number
}

export type MoveDecisions = Partial<Record<string, MoveDecision>>

/** `codes` empty means the whole indication. */
export interface MoveEffectChange {
  connectorIds: string[]
  indicationId: string
  name: string
  codes: string[]
}

export interface MoveEffectivePreview {
  groups: {
    connectorIds: string[]
    effectiveIndications: Indication[]
    ddeRows: DDERow[]
    diff: IndicationSetDiff
  }[]
  dropped: MoveEffectChange[]
  added: MoveEffectChange[]
}

export interface MovePreviewResponse {
  inForceRevisionNumber?: number
  scheduledRevision?: {
    id: string
    revisionNumber: number
    effectiveStart: string
  }
  from: {
    /** The draft's seed revision, or null, when the move starts from the draft. */
    revisionId: string | null
    revisionNumber: number
    /** Set when the move starts from the draft: the version the preview was built from. */
    draftVersion?: number
    /** Set when the move starts from the draft; a draft replaced since has a new id. */
    draftId?: string
    standardRevisionId: string
    standardRevisionNumber: number
  }
  to: {
    standardRevisionId: string
    standardRevisionNumber: number
    snapshotHash: string
  }
  standardDiff: VersionCompareGroup
  /** Eligible Standard revisions after the base, up to the target, oldest first. */
  standardSteps: {
    revisionId: string
    revisionNumber: number
    createdAt: string
    createdBy: string
    /** compareNodes(previous step, this step): the same shape as standardDiff. */
    diff: VersionCompareGroup
  }[]
  rows: MoveRow[]
  readiness: MoveReadiness
  effectivePreview: MoveEffectivePreview
}

export interface MoveRequest {
  /** Omitted with `source: 'draft'`, which sends `expectedDraftVersion` instead. */
  fromRevisionId?: string
  source?: 'draft'
  expectedDraftVersion?: number
  expectedDraftId?: string
  toStandardRevisionId: string
  expectedSnapshotHash: string
  decisions: MoveDecisions
  replaceDraft?: boolean
  /** Attributes the operations the move generates; the system actor when absent. */
  actor?: OperationActor
}

export interface RebasedOverrides {
  rows: MoveRow[]
  /** Undecided conflicts are left out, which is what the preview shows. */
  buildDraft: (decisions?: MoveDecisions) => ConfigurationScopedOverride[]
}

const KEEP_DROP: MoveDecision[] = ['keep', 'drop']

const MOVE_ACTOR: OperationActor = {
  id: 'system:standard-move',
  displayName: 'Move to newer Standard',
}

const KIND_LABELS: Record<ConfigurationOperation['kind'], string> = {
  set_indication_details: 'Description',
  set_hcpcs_codes: 'HCPCS codes',
  set_age_rule: 'Age',
  set_dosing_rule: 'Dosing',
  set_frequency_rule: 'Frequency',
  set_max_daily_units_rule: 'Max units per day',
  set_max_units_over_time_rule: 'Max units over time',
  upsert_diagnosis_code: 'Diagnosis code',
  remove_diagnosis_code: 'Diagnosis code',
}

const FIELD_LABELS: Record<string, string> = {
  ...INDICATION_FIELD_LABELS,
  age: 'Age',
  description: 'Description',
  dosingSummary: 'Dosing summary',
  patientAttribute: 'Patient attribute',
  patientAttributeValue: 'Patient attribute value',
}

const isDiagnosis = (field: string) => field.startsWith('icd10Codes.')

const inDefinedList = (code: unknown) =>
  !!code && isIcd10InDefinedList(code as IndicationIcd10Code)

const formatValue = (field: string, value: unknown): string => {
  if (isDiagnosis(field))
    return value
      ? inDefinedList(value)
        ? 'Included'
        : 'Excluded'
      : 'Not listed'
  if (value === undefined || value === null || value === '') return 'Not set'
  if (Array.isArray(value)) return value.length ? value.join(', ') : 'None'
  return String(value)
}

const display = (indication: Indication | undefined, fields: string[]) => {
  if (!indication) return 'Not in Standard'
  const shown = fields.filter((field) => !field.endsWith('_json'))
  return shown
    .map((field) => {
      const value = formatValue(field, operationFieldValue(indication, field))
      return shown.length > 1
        ? `${FIELD_LABELS[field] ?? field} ${value}`
        : value
    })
    .join(' · ')
}

const fieldLabel = (operation: ConfigurationOperation) =>
  operation.kind === 'upsert_diagnosis_code' ||
  operation.kind === 'remove_diagnosis_code'
    ? `Diagnosis code ${operation.value.code}`
    : KIND_LABELS[operation.kind]

const outcomeOf = (
  { operation, classification }: ReconciledOperation,
  from: Indication | undefined,
  to: Indication,
): Pick<MoveRow, 'outcome' | 'standardChangedAround'> => {
  const fields = operationFields(operation)
  const applied = applyConfigurationOperation(to, operation)
  const sameExport = fields.every(
    (field) =>
      isDiagnosis(field) &&
      inDefinedList(operationFieldValue(to, field)) ===
        inDefinedList(operationFieldValue(applied, field)),
  )
  if (classification === 'redundant' || sameExport)
    return {
      outcome:
        fields.every(isDiagnosis) &&
        !inDefinedList(operationFieldValue(applied, fields[0]!))
          ? 'no_longer_needed'
          : 'now_matches_standard',
    }
  if (
    !from ||
    fields.some(
      (field) =>
        !same(operationFieldValue(from, field), operationFieldValue(to, field)),
    )
  )
    return { outcome: 'conflict_field' }
  return {
    outcome: 'still_applies',
    ...(classification !== 'carried_unchanged' && {
      standardChangedAround: true,
    }),
  }
}

const decisionsFor = (
  outcome: MoveOutcome,
  hasSource = true,
): Pick<MoveRow, 'defaultDecision' | 'allowedDecisions'> => {
  switch (outcome) {
    case 'still_applies':
    case 'carries_over':
      return { defaultDecision: 'keep', allowedDecisions: KEEP_DROP }
    case 'now_matches_standard':
    case 'no_longer_needed':
      return {
        defaultDecision: 'drop',
        allowedDecisions: hasSource ? KEEP_DROP : ['drop'],
      }
    case 'conflict_field':
      return { allowedDecisions: ['keep', 'take_standard'] }
    case 'conflict_removed':
      return {
        allowedDecisions: hasSource ? ['keep_as_added', 'drop'] : ['drop'],
      }
    case 'conflict_collision':
      return { allowedDecisions: ['keep_as_modification', 'drop'] }
  }
}

/** Only the fields an added indication may carry, and only codes it exports. */
const authorable = (indication: Indication): Indication =>
  Object.fromEntries(
    Object.keys(addedIndicationSchema.entries).flatMap((key) => {
      const value =
        key === 'icd10Codes'
          ? indication.icd10Codes?.filter(isIcd10InDefinedList)
          : indication[key as keyof Indication]
      return value === undefined || value === null ? [] : [[key, value]]
    }),
  ) as unknown as Indication

const PATCH_GROUPS = [
  ['set_indication_details', ['description']],
  ['set_age_rule', ['age', 'ageMin', 'ageMinUom', 'ageMax', 'ageMaxUom']],
  [
    'set_dosing_rule',
    [
      'dosingSummary',
      'dosingSummary_json',
      'patientAttribute',
      'patientAttributeValue',
    ],
  ],
  [
    'set_frequency_rule',
    ['frequencyMaxAdminDays', 'frequencyMaxFrequency', 'frequencyTimePeriod'],
  ],
  [
    'set_max_units_over_time_rule',
    [
      'maxUnitsOverTimeMaxHCPCSUnits',
      'maxUnitsOverHcpcsMaxFrequency',
      'maxUnitsOverHcpcsTimePeriod',
    ],
  ],
] as const

/** The operations that turn `base` into `target`, as `set_*` patches and per-code diagnosis edits. */
const operationsBetween = (
  base: Indication,
  target: Indication,
  envelope: (
    kind: ConfigurationOperation['kind'],
    key: string,
    value: unknown,
  ) => ConfigurationOperation,
): ConfigurationOperation[] => {
  const field = (indication: Indication, key: string) =>
    indication[key as keyof Indication] ?? null
  const patches = PATCH_GROUPS.flatMap(([kind, keys]) => {
    const changed = keys.filter(
      (key) => !same(field(base, key), field(target, key)),
    )
    return changed.length
      ? [
          envelope(
            kind,
            kind,
            Object.fromEntries(changed.map((key) => [key, field(target, key)])),
          ),
        ]
      : []
  })
  const scalars = [
    ...(same(base.hcpcsCodes ?? [], target.hcpcsCodes ?? [])
      ? []
      : [envelope('set_hcpcs_codes', 'hcpcs', target.hcpcsCodes ?? [])]),
    ...(same(
      field(base, 'maxDailyHcpcsUnits'),
      field(target, 'maxDailyHcpcsUnits'),
    )
      ? []
      : [
          envelope(
            'set_max_daily_units_rule',
            'max-daily',
            field(target, 'maxDailyHcpcsUnits'),
          ),
        ]),
  ]
  const key = (code: string) => code.trim().toUpperCase()
  const baseCodes = new Map(
    (base.icd10Codes ?? []).map((code) => [key(code.code), code]),
  )
  const targetCodes = new Map(
    (target.icd10Codes ?? []).map((code) => [key(code.code), code]),
  )
  const diagnoses = [
    ...[...targetCodes].flatMap(([code, value]) =>
      same(baseCodes.get(code), value)
        ? []
        : [envelope('upsert_diagnosis_code', `diagnosis:${code}`, value)],
    ),
    ...[...baseCodes.keys()].flatMap((code) =>
      targetCodes.has(code)
        ? []
        : [envelope('remove_diagnosis_code', `diagnosis:${code}`, { code })],
    ),
  ]
  return [...patches, ...scalars, ...diagnoses]
}

type Decide = (row: MoveRow) => MoveDecision | undefined

interface OverridePlan {
  rows: MoveRow[]
  build: (decide: Decide) => ConfigurationScopedOverride[]
}

const rowId = (override: ConfigurationScopedOverride, operationId?: string) =>
  [
    override.behavior,
    override.indicationId,
    override.connectorIds.join(','),
    ...(operationId ? [operationId] : []),
  ].join('|')

/**
 * Rebases a workspace's committed overrides from one Standard revision onto a
 * newer one. Classification runs against the enforced Standards, the same
 * view `materializeConfiguration` exports, so interval redundancy agrees with
 * what ships. `source` must be in literal (format 2) intervals.
 */
export const rebaseOverrides = ({
  source,
  from: authoredFrom,
  to: authoredTo,
  now = new Date().toISOString(),
  actor = MOVE_ACTOR,
  steps = [],
}: {
  source: ConfigurationScopedOverride[]
  from: StandardRevision
  to: StandardRevision
  now?: string
  actor?: OperationActor
  /** The Standards after `from`, in order, up to and including `to`; rows then say which steps changed them. */
  steps?: StandardRevision[]
}): RebasedOverrides => {
  const from = enforcedStandard(authoredFrom)
  const to = enforcedStandard(authoredTo)
  const indicationsById = (standard: StandardRevision) =>
    new Map(standard.indications.map((item) => [item.id, item]))
  const fromById = indicationsById(from)
  const toById = indicationsById(to)
  const enforcedSteps = [from, ...steps.map(enforcedStandard)].map(
    indicationsById,
  )
  const standardSteps = steps.map(({ ref }, index) => ({
    revisionNumber: ref.revisionNumber,
    previous: enforcedSteps[index]!,
    current: enforcedSteps[index + 1]!,
  }))
  const revisionsWhere = (
    indicationId: string,
    changed: (previous?: Indication, current?: Indication) => boolean,
  ) => {
    const revisionNumbers = standardSteps.flatMap(
      ({ revisionNumber, previous, current }) =>
        changed(previous.get(indicationId), current.get(indicationId))
          ? [revisionNumber]
          : [],
    )
    return revisionNumbers.length ? revisionNumbers : undefined
  }
  const attribution = (
    indicationId: string,
    changed: (previous?: Indication, current?: Indication) => boolean,
  ) => {
    const rowChangedIn = revisionsWhere(indicationId, changed)
    const indicationChangedIn = revisionsWhere(
      indicationId,
      (previous, current) => !same(previous, current),
    )
    return {
      ...(rowChangedIn && { changedIn: rowChangedIn }),
      ...(indicationChangedIn && { indicationChangedIn }),
    }
  }

  const plan = (override: ConfigurationScopedOverride): OverridePlan => {
    const fromIndication = fromById.get(override.indicationId)
    const toIndication = toById.get(override.indicationId)
    const base = {
      indicationId: override.indicationId,
      indicationName:
        toIndication?.name ??
        fromIndication?.name ??
        override.addedIndication?.name ??
        override.indicationId,
      connectorIds: override.connectorIds,
      ...(override.rationale && { rationale: override.rationale }),
    }
    const reconciled = reconcileConfiguration({
      oldStandard: from,
      newStandard: to,
      operations: override.behavior === 'modified' ? override.operations : [],
      tombstonedIndicationIds:
        override.behavior === 'not_applied' ? [override.indicationId] : [],
      addedIndicationIds:
        override.behavior === 'added' ? [override.indicationId] : [],
    })

    if (override.behavior === 'added') {
      const collides =
        reconciled.addedIndications[0]?.classification === 'conflicting'
      const outcome = collides ? 'conflict_collision' : 'carries_over'
      const row: MoveRow = {
        ...base,
        id: rowId(override),
        field: 'Added indication',
        overrideValue: 'Added for this client',
        fromValue: 'Not in Standard',
        toValue: collides ? 'In Standard' : 'Not in Standard',
        outcome,
        ...decisionsFor(outcome),
        ...(collides &&
          attribution(
            override.indicationId,
            (previous, current) => !previous && !!current,
          )),
      }
      return {
        rows: [row],
        build: (decide) => {
          const decision = decide(row)
          if (decision === 'keep') return [override]
          if (decision !== 'keep_as_modification' || !toIndication) return []
          const operations = operationsBetween(
            toIndication,
            override.addedIndication!,
            (kind, key, value) =>
              ({
                id: `${row.id}|${key}`,
                schemaVersion: CONFIGURATION_OPERATION_SCHEMA_VERSION,
                kind,
                indicationId: override.indicationId,
                value,
                ...(override.rationale && { rationale: override.rationale }),
                ...(override.provenance && {
                  provenance: override.provenance,
                }),
                actor,
                createdAt: now,
              }) as ConfigurationOperation,
          )
          return operations.length
            ? [
                {
                  indicationId: override.indicationId,
                  connectorIds: override.connectorIds,
                  behavior: 'modified',
                  operations,
                  ...(override.rationale && { rationale: override.rationale }),
                  ...(override.provenance && {
                    provenance: override.provenance,
                  }),
                },
              ]
            : []
        },
      }
    }

    if (override.behavior === 'not_applied') {
      const removal = reconciled.removedIndications[0]
      const outcome = toIndication ? 'still_applies' : 'no_longer_needed'
      const row: MoveRow = {
        ...base,
        id: rowId(override),
        field: 'Not applied',
        overrideValue: 'Not applied',
        fromValue: fromIndication ? 'Applies' : 'Not in Standard',
        toValue: toIndication ? 'Applies' : 'Removed from Standard',
        outcome,
        ...decisionsFor(outcome, !!toIndication),
        ...(toIndication &&
          removal?.classification !== 'carried_unchanged' && {
            standardChangedAround: true,
          }),
        ...attribution(
          override.indicationId,
          (previous, current) => !!previous && !same(previous, current),
        ),
      }
      return {
        rows: [row],
        build: (decide) => (decide(row) === 'keep' ? [override] : []),
      }
    }

    if (override.behavior !== 'modified') return { rows: [], build: () => [] }

    if (!toIndication) {
      const fields = override.operations.flatMap(operationFields)
      const row: MoveRow = {
        ...base,
        id: rowId(override),
        field: [...new Set(override.operations.map(fieldLabel))].join(', '),
        overrideValue: fromIndication
          ? display(
              override.operations.reduce(
                applyConfigurationOperation,
                fromIndication,
              ),
              fields,
            )
          : 'Not in Standard',
        fromValue: display(fromIndication, fields),
        toValue: 'Removed from Standard',
        outcome: 'conflict_removed',
        ...decisionsFor('conflict_removed', !!fromIndication),
        ...attribution(
          override.indicationId,
          (previous, current) => !!previous && !current,
        ),
      }
      return {
        rows: [row],
        build: (decide) =>
          decide(row) === 'keep_as_added' && fromIndication
            ? [
                {
                  indicationId: override.indicationId,
                  connectorIds: override.connectorIds,
                  behavior: 'added',
                  operations: [],
                  addedIndication: authorable(
                    override.operations.reduce(
                      applyConfigurationOperation,
                      fromIndication,
                    ),
                  ),
                  rationale:
                    override.rationale ??
                    override.operations.find(({ rationale }) => rationale)
                      ?.rationale ??
                    `Kept for this client after Standard r${authoredTo.ref.revisionNumber} removed it`,
                  provenance: override.provenance?.source
                    ? override.provenance
                    : {
                        source: 'standard_revision',
                        sourceId: authoredFrom.ref.id,
                      },
                },
              ]
            : [],
      }
    }

    const rows = reconciled.operations.map((item): MoveRow => {
      const { operation } = item
      const fields = operationFields(operation)
      const classified = outcomeOf(item, fromIndication, toIndication)
      return {
        ...base,
        id: rowId(override, operation.id),
        field: fieldLabel(operation),
        overrideValue:
          operation.kind === 'remove_diagnosis_code'
            ? 'Removed'
            : display(
                applyConfigurationOperation(
                  fromIndication ?? toIndication,
                  operation,
                ),
                fields,
              ),
        fromValue: display(fromIndication, fields),
        toValue: display(toIndication, fields),
        ...(operation.rationale && { rationale: operation.rationale }),
        ...classified,
        ...decisionsFor(classified.outcome),
        ...attribution(override.indicationId, (previous, current) =>
          previous && current
            ? fields.some(
                (field) =>
                  !same(
                    operationFieldValue(previous, field),
                    operationFieldValue(current, field),
                  ),
              )
            : previous !== current,
        ),
      }
    })
    return {
      rows,
      build: (decide) => {
        const operations = reconciled.operations
          .filter((_, index) => decide(rows[index]!) === 'keep')
          .map(({ operation }) => operation)
        return operations.length ? [{ ...override, operations }] : []
      },
    }
  }

  const plans = source.map(plan)
  return {
    rows: plans.flatMap(({ rows }) => rows),
    buildDraft: (decisions = {}) => {
      const decide: Decide = (row) => {
        const chosen = decisions[row.id]
        return chosen && row.allowedDecisions.includes(chosen)
          ? chosen
          : row.defaultDecision
      }
      return plans
        .flatMap(({ build }) => build(decide))
        .map((override) => normalizeScopedOverride(override, authoredTo, true))
        .sort(
          (left, right) =>
            compareCodeUnits(left.indicationId, right.indicationId) ||
            compareCodeUnits(
              left.connectorIds.join(','),
              right.connectorIds.join(','),
            ),
        )
    },
  }
}

export const moveReadiness = (
  rows: Pick<MoveRow, 'outcome' | 'standardChangedAround'>[],
): MoveReadiness => {
  const count = (test: (row: (typeof rows)[number]) => boolean) =>
    rows.filter(test).length
  const applies = ({ outcome }: (typeof rows)[number]) =>
    outcome === 'still_applies' || outcome === 'carries_over'
  const conflicts = count(({ outcome }) => outcome.startsWith('conflict_'))
  const redundant = count(
    ({ outcome }) =>
      outcome === 'now_matches_standard' || outcome === 'no_longer_needed',
  )
  const changedAround = count(
    (row) => applies(row) && !!row.standardChangedAround,
  )
  const carryOver = count((row) => applies(row) && !row.standardChangedAround)
  return {
    status: conflicts
      ? 'conflicts'
      : changedAround + redundant
        ? 'review'
        : 'ready',
    conflicts,
    carryOver,
    changedAround,
    redundant,
  }
}

/** Conflict rows the author has not decided, by row id. */
export const undecidedMoveRows = (
  rows: MoveRow[],
  decisions: MoveDecisions,
): string[] =>
  rows
    .filter(
      (row) =>
        !row.defaultDecision &&
        !row.allowedDecisions.includes(decisions[row.id]!),
    )
    .map(({ id }) => id)
